/**
 * Date calculation utilities
 */

import { getEffectiveDurationMinutes, minutesToMs } from '../../../../utils/taskDuration';
import { MS_PER_DAY, ZOOM_PADDING } from './constants';
import { getTimelineScale } from './zoomUtils';

/**
 * Get start and end dates for a task (for Gantt bar positioning)
 *
 * Right edge (end of bar):
 * - If estimatedDurationMinutes is set, start + that duration
 * - Else if dueDate exists, the right edge equals the due date
 * - Else default 1 day width
 */
export function getTaskDates(task) {
  let startDate = null;
  if (task.startedAt) {
    startDate = new Date(task.startedAt);
  } else if (task.createdAt) {
    startDate = new Date(task.createdAt);
  }

  if (!startDate) {
    return null;
  }

  const estMin = getEffectiveDurationMinutes(task);

  let endDate;
  if (estMin && estMin > 0) {
    endDate = new Date(startDate.getTime() + minutesToMs(estMin));
  } else if (task.dueDate) {
    endDate = new Date(task.dueDate);
    if (endDate <= startDate) {
      endDate = new Date(startDate.getTime() + MS_PER_DAY);
    }
  } else {
    endDate = new Date(startDate.getTime() + MS_PER_DAY);
  }

  const dueDate = task.dueDate ? new Date(task.dueDate) : null;

  return {
    startDate,
    endDate,
    dueDate,
    hasEstimate: !!(estMin && estMin > 0),
  };
}

/**
 * Calculate the overall date range for all tasks
 * Always includes "today" in the visible range
 * Can be extended with extraRange to include preset dates
 */
export function calculateDateRange(tasks, zoomLevel = 'day', extraRange = null) {
  if (tasks.length === 0) return null;

  let minDate = null;
  let maxDate = null;

  // Get bounds from tasks
  tasks.forEach(task => {
    const dates = getTaskDates(task);
    if (!dates) return;

    if (!minDate || dates.startDate < minDate) {
      minDate = dates.startDate;
    }
    if (!maxDate || dates.endDate > maxDate) {
      maxDate = dates.endDate;
    }
  });

  if (!minDate || !maxDate) return null;

  // Always include today in the range
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (today < minDate) minDate = today;
  if (today > maxDate) maxDate = new Date(today.getTime() + MS_PER_DAY);

  // Expand range if extraRange (from preset) extends beyond tasks
  if (extraRange && extraRange.startDate && extraRange.endDate) {
    if (extraRange.startDate < minDate) minDate = extraRange.startDate;
    if (extraRange.endDate > maxDate) maxDate = extraRange.endDate;
  }

  // Add padding - use fixed generous padding when zoomLevel is null (prevents jolt during zoom)
  // When zoomLevel is null, we use a large fixed padding so timeline boundaries stay stable
  const paddingDays = zoomLevel === null ? 90 : (ZOOM_PADDING[zoomLevel] ?? 14);
  
  // Normalize minDate to midnight first, then apply padding
  // This ensures day columns align properly across all zoom levels
  const minDateMidnight = new Date(minDate);
  minDateMidnight.setHours(0, 0, 0, 0);
  
  const maxDateMidnight = new Date(maxDate);
  maxDateMidnight.setHours(0, 0, 0, 0);
  
  // Apply padding (in whole days from midnight)
  const paddedStart = new Date(minDateMidnight.getTime() - paddingDays * MS_PER_DAY);
  const paddedEnd = new Date(maxDateMidnight.getTime() + (paddingDays + 1) * MS_PER_DAY); // +1 to include the end day
  
  const totalDays = Math.round((paddedEnd - paddedStart) / MS_PER_DAY);

  return {
    startDate: paddedStart,
    endDate: paddedEnd,
    totalDays,
    scale: getTimelineScale(zoomLevel),
  };
}

/**
 * Calculate position (in %) for a date within the range
 */
export function calculatePosition(date, startDate, totalDays) {
  const daysSinceStart = (date - startDate) / MS_PER_DAY;
  return (daysSinceStart / totalDays) * 100;
}

/**
 * Calculate width (in %) for a duration
 */
export function calculateWidth(startDate, endDate, viewStartDate, totalDays) {
  const startPos = calculatePosition(startDate, viewStartDate, totalDays);
  const endPos = calculatePosition(endDate, viewStartDate, totalDays);
  return endPos - startPos;
}
